const StateJS = require('@steelbreeze/state');
const EventEmitter = require('events').EventEmitter;
const Enum = require('./enums');
const Event = require('./events');

/*
 * Definition for type : MediaSource
 */

function MediaSource(name, root) {
  this.bus = (root === null) ? new EventEmitter() : root.bus;
  this.name = name;
  this.root = (root === null) ? this : root;
  this.ready = false;

  this.build(name);
}

MediaSource.prototype.build = function (session) {
  /* State machine (states and regions) */
  /* Building root component */
  this._statemachine = new StateJS.State('default_statemachine');
  const _initial_MediaSource = new StateJS.PseudoState('_initial', this._statemachine, StateJS.PseudoStateKind.Initial);
  const MediaSource_null_Reset = new StateJS.State('Reset', this._statemachine).entry(() => {
    this.clearMediaSource(false);
  });
  const MediaSource_null_Idle = new StateJS.State('Idle', this._statemachine);
  const MediaSource_null_Connected = new StateJS.State('Connected', this._statemachine);
  const MediaSource_null_Connected_Setup = new StateJS.State('Setup', MediaSource_null_Connected).entry(() => {
    this.MediaSource_null_Connected_Setup_startedAt_var = this.timestamp();
    this.checkFormatSupport();
    this.createNewSource();
  }).exit(() => {
    this.bus.emit('statusInput', new Event.Duration_StatusMsg(this.name, 'statusInput', Enum.DurationTypes_ENUM.NEDIASOURCESETUP, this.timestamp() - this.MediaSource_null_Connected_Setup_startedAt_var));
  });
  const MediaSource_null_Connected_Playing = new StateJS.State('Playing', MediaSource_null_Connected).entry(() => {
    // reset properties
    this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var = 0;
  });
  const MediaSource_null_Connected_Playing_CheckQueue = new StateJS.State('CheckQueue', MediaSource_null_Connected_Playing);
  const MediaSource_null_Connected_Playing_DeQueue = new StateJS.State('DeQueue', MediaSource_null_Connected_Playing).entry(() => {
    const data_var = (this.MediaSource_fifo_var.shift());
    this.appendBuffer(data_var);
  });
  const MediaSource_null_Connected_Playing_Updating = new StateJS.State('Updating', MediaSource_null_Connected_Playing).entry(() => {
    this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var += 1;
  });
  const MediaSource_null_Connected_Playing_Resting = new StateJS.State('Resting', MediaSource_null_Connected_Playing);
  const MediaSource_null_Connected_Playing_RemovingOldVideo = new StateJS.State('RemovingOldVideo', MediaSource_null_Connected_Playing).entry(() => {
    this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var = 0;
    this.MediaSource_null_Connected_Playing_RemovingOldVideo_hadToRemoveVideo_var = this.removeOldVideo();
  });
  const _initial_MediaSource_null_Connected_Playing = new StateJS.PseudoState('_initial', MediaSource_null_Connected_Playing, StateJS.PseudoStateKind.Initial);
  _initial_MediaSource_null_Connected_Playing.to(MediaSource_null_Connected_Playing_CheckQueue);
  const _initial_MediaSource_null_Connected = new StateJS.PseudoState('_initial', MediaSource_null_Connected, StateJS.PseudoStateKind.Initial);
  _initial_MediaSource_null_Connected.to(MediaSource_null_Connected_Setup);
  _initial_MediaSource.to(MediaSource_null_Reset);
  MediaSource_null_Connected_Playing_DeQueue.to(MediaSource_null_Connected_Playing_Updating);
  MediaSource_null_Connected_Playing_RemovingOldVideo.to(MediaSource_null_Connected_Playing_Updating).when(() => (this.MediaSource_null_Connected_Playing_RemovingOldVideo_hadToRemoveVideo_var === true));
  MediaSource_null_Connected_Playing_RemovingOldVideo.to(MediaSource_null_Connected_Playing_CheckQueue).when(() => (this.MediaSource_null_Connected_Playing_RemovingOldVideo_hadToRemoveVideo_var === false));
  MediaSource_null_Connected_Playing_CheckQueue.to(MediaSource_null_Connected_Playing_DeQueue).when(() => (this.MediaSource_fifo_var.length > 0));
  MediaSource_null_Connected_Playing_CheckQueue.to(MediaSource_null_Connected_Playing_Resting).when(() => (this.MediaSource_fifo_var.length === 0));
  MediaSource_null_Reset.to(MediaSource_null_Idle);
  MediaSource_null_Connected_Playing_Updating.to(MediaSource_null_Connected_Playing_CheckQueue).on(Event.Updated_MediaSource).when((updated) => updated.port === 'mediaSourcePort' && updated.type === 'updated' && (this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var < this.MediaSource_maxUpdatesWithoutRemovingOldVideo_var));
  MediaSource_null_Connected_Playing_Updating.to(MediaSource_null_Connected_Playing_RemovingOldVideo).on(Event.Updated_MediaSource).when((updated) => updated.port === 'mediaSourcePort' && updated.type === 'updated' && (this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var >= this.MediaSource_maxUpdatesWithoutRemovingOldVideo_var));
  MediaSource_null_Connected.to(MediaSource_null_Reset).on(Event.ResetMS_MediaSource).when((resetMS) => resetMS.port === 'mediaSourcePort' && resetMS.type === 'resetMS');
  MediaSource_null_Connected_Setup.to(MediaSource_null_Connected_Playing).on(Event.Ready_MediaSource).when((ready) => ready.port === 'mediaSourcePort' && ready.type === 'ready');
  MediaSource_null_Connected.to(MediaSource_null_Idle).on(Event.Stopped_WebsocketStreamMsg).when((stopped) => stopped.port === 'websocket' && stopped.type === 'stopped');
  MediaSource_null_Connected.to(MediaSource_null_Reset).on(Event.Timeout_WebsocketStreamMsg).when((timeout) => timeout.port === 'websocket' && timeout.type === 'timeout');
  this._statemachine.on(Event.VideoPacket).when((videoPacket) => videoPacket.port === 'websocket' && videoPacket.type === 'videoPacket').effect((videoPacket) => {
    this.queueData(videoPacket.data);
  });
  MediaSource_null_Connected_Playing_Resting.to(MediaSource_null_Connected_Playing_Updating).on(Event.VideoPacket_WebsocketStreamMsg).when((videoPacket) => videoPacket.port === 'websocket' && videoPacket.type === 'videoPacket').effect((videoPacket) => {
    this.appendBuffer(videoPacket.data);
  });
  MediaSource_null_Connected.to(MediaSource_null_Reset).on(Event.StreamError_WebsocketStreamMsg).when((streamError) => streamError.port === 'websocket' && streamError.type === 'streamError');
  MediaSource_null_Connected_Playing.to(MediaSource_null_Connected_Setup).on(Event.FormatPacket_WebsocketStreamMsg).when((formatPacket) => formatPacket.port === 'websocket' && formatPacket.type === 'formatPacket').effect((formatPacket) => {
    this.MediaSource_sourceFormat_var = formatPacket.format;
  });
  MediaSource_null_Idle.to(MediaSource_null_Connected).on(Event.FormatPacket_WebsocketStreamMsg).when((formatPacket) => formatPacket.port === 'websocket' && formatPacket.type === 'formatPacket').effect((formatPacket) => {
    this.MediaSource_sourceFormat_var = formatPacket.format;
  });
  MediaSource_null_Connected.to(MediaSource_null_Reset).on(Event.VideoElement_Html5VideoElementMsg).when((videoElement) => videoElement.port === 'html5VideoElement' && videoElement.type === 'videoElement').effect((videoElement) => {
    this.MediaSource_video_var = videoElement.video;
  });
  MediaSource_null_Idle.to(MediaSource_null_Reset).on(Event.VideoElement_Html5VideoElementMsg).when((videoElement) => videoElement.port === 'html5VideoElement' && videoElement.type === 'videoElement').effect((videoElement) => {
    this.MediaSource_video_var = videoElement.video;
  });
  MediaSource_null_Connected.to(MediaSource_null_Reset).on(Event.MediaSourceStop_MediaSourceMsg).when((mediaSourceStop) => mediaSourceStop.port === 'mediaSource' && mediaSourceStop.type === 'mediaSourceStop');
};
MediaSource.prototype.timestamp = function () {
  return Date.now();
};

MediaSource.prototype.clearMediaSource = function (MediaSource_clearMediaSource_force_var) {
  if (MediaSource_clearMediaSource_force_var && (this.MediaSource_sourceBuffer_var != null)) {
    try {
      const start = this.MediaSource_sourceBuffer_var.buffered.start(this.MediaSource_sourceBuffer_var.buffered.length - 1);
      const end = this.MediaSource_video_var.currentTime;
      this.MediaSource_sourceBuffer_var.remove(start, end);
    } catch (error) {}
  }
  this.MediaSource_sourceFormat_var = '';
  this.MediaSource_mediaSource_var = undefined;
  this.MediaSource_sourceBuffer_var = undefined;
  this.MediaSource_fifo_var = [];
};

MediaSource.prototype.createNewSource = function () {
  this.MediaSource_fifo_var = [];
  try {
    this.MediaSource_mediaSource_var = window.ManagedMediaSource ? new window.ManagedMediaSource() : new window.MediaSource();
    if ('ManagedMediaSource' in window) {
      this.MediaSource_video_var.disableRemotePlayback = true;
      this.MediaSource_video_var.srcObject = this.MediaSource_mediaSource_var;
    } else {
      this.MediaSource_video_var.src = window.URL.createObjectURL(this.MediaSource_mediaSource_var);
    }
    this.MediaSource_mediaSource_var.addEventListener('sourceopen', () => {
      if (this.MediaSource_mediaSource_var && this.MediaSource_mediaSource_var.readyState === 'open') {
        this.attachSourceBuffer();
      }
    });
  } catch (error) {
    console.log(error);
    this.MediaSource_client_var.logException(error);

    this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
  }
};

MediaSource.prototype.attachSourceBuffer = function () {
  try {
    // ios
    if ('ManagedMediaSource' in window) {
      // https://stackoverflow.com/questions/74461792/mediasource-api-safari-pauses-video-on-buffer-underflow
      this.MediaSource_mediaSource_var.duration = Number.POSITIVE_INFINITY;
    }
    this.MediaSource_sourceBuffer_var = (this.MediaSource_mediaSource_var.addSourceBuffer(this.MediaSource_sourceFormat_var));
    this.MediaSource_sourceBuffer_var.addEventListener('error', (error) => {
      console.error(error);
      this.MediaSource_client_var.logException(error);

      this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
    });
    this.MediaSource_sourceBuffer_var.addEventListener('updateend', () => {
      this.bus.emit('mediaSourcePort', new Event.Updated_MediaSource(this.name, 'mediaSourcePort'));
    });
    if ('ManagedMediaSource' in window) {
      this.MediaSource_video_var.play().then(() => {}).catch((error) => {
        this.MediaSource_client_var.logException(error);

        this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
      });
    }
    this.bus.emit('mediaSourcePort', new Event.Ready_MediaSource(this.name, 'mediaSourcePort'));
  } catch (error) {
    console.error(error);
    this.MediaSource_client_var.logException(error);

    this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
  }
};

MediaSource.prototype.checkFormatSupport = function () {
  let supported;
  try {
    supported = window.ManagedMediaSource ? window.ManagedMediaSource.isTypeSupported(this.MediaSource_sourceFormat_var) : window.MediaSource && window.MediaSource.isTypeSupported(this.MediaSource_sourceFormat_var);
  } catch (error) {
    console.error(error);
    this.MediaSource_client_var.logException(error);
    supported = false;
  }

  if (!supported) {
    this.bus.emit('mediaSource', new Event.MediaSourceUnsupported_MediaSourceMsg(this.name, 'mediaSource'));
    this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
  }
};

MediaSource.prototype.queueData = function (MediaSource_queueData_data_var) {
  this.MediaSource_fifo_var.push(MediaSource_queueData_data_var);
};

MediaSource.prototype.appendBuffer = function (MediaSource_appendBuffer_data_var) {
  try {
    this.MediaSource_sourceBuffer_var.appendBuffer(MediaSource_appendBuffer_data_var);
  } catch (error) {
    console.error(error);
    this.MediaSource_client_var.logException(error);

    this.bus.emit('mediaSource', new Event.MediaSourceFailure_MediaSourceMsg(this.name, 'mediaSource'));
    this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
  }
};

MediaSource.prototype.removeOldVideo = function () {
  try {
    if (this.MediaSource_sourceBuffer_var.buffered.length === 0) {
      return false;
    }
    const start = this.MediaSource_sourceBuffer_var.buffered.start(this.MediaSource_sourceBuffer_var.buffered.length - 1);
    const end = this.MediaSource_video_var.currentTime - this.MediaSource_secondsToKeep_var;
    if (end > start) {
      this.MediaSource_sourceBuffer_var.remove(start, end);

      return true;
    }

    return false;
  } catch (error) {
    console.error(error);
    this.MediaSource_client_var.logException(error);

    this.bus.emit('mediaSourcePort', new Event.ResetMS_MediaSource(this.name, 'mediaSourcePort'));
    this.bus.emit('mediaSource', new Event.MediaSourceFailure_MediaSourceMsg(this.name, 'mediaSource'));
  }
  return false;
};

MediaSource.prototype._stop = function () {
  this.root = null;
  this.ready = false;
  this.stopped = true;
};

MediaSource.prototype._delete = function () {
  this._statemachine = null;
  this._null_instance = null;
  this.bus.removeAllListeners();
};

MediaSource.prototype._init = function () {
  this._null_instance = new StateJS.Instance('null_instance', this._statemachine);
  this.ready = true;
};

MediaSource.prototype._receive = function (msg) {
  if (this.ready) {
    this._null_instance.evaluate(msg);
  } else if (!this.stopped) {
    setTimeout(() => this._receive(msg), 4);
  }
};

MediaSource.prototype.toString = function () {
  let result = `instance ${this.name}:${this.constructor.name}\n`;
  result += `\n\tupdatesWithoutRemovingOldVideo = ${this.MediaSource_null_Connected_Playing_updatesWithoutRemovingOldVideo_var}`;
  result += `\n\tvideo = ${this.MediaSource_video_var}`;
  result += `\n\tmaxUpdatesWithoutRemovingOldVideo = ${this.MediaSource_maxUpdatesWithoutRemovingOldVideo_var}`;
  result += `\n\tsourceBuffer = ${this.MediaSource_sourceBuffer_var}`;
  result += `\n\tfifo = ${this.MediaSource_fifo_var}`;
  result += `\n\tstartedAt = ${this.MediaSource_null_Connected_Setup_startedAt_var}`;
  result += `\n\tclient = ${this.MediaSource_client_var}`;
  result += `\n\tmediaSource = ${this.MediaSource_mediaSource_var}`;
  result += `\n\tsourceFormat = ${this.MediaSource_sourceFormat_var}`;
  result += `\n\thadToRemoveVideo = ${this.MediaSource_null_Connected_Playing_RemovingOldVideo_hadToRemoveVideo_var}`;
  result += `\n\tsecondsToKeep = ${this.MediaSource_secondsToKeep_var}`;
  result += '';
  return result;
};

module.exports = MediaSource;
